/**
 * usePWAValueMoment — tracks the three Hantavirus value-moment triggers.
 *
 * Hantavirus value moments (differ from WeAutomate's):
 *   1. dashboard_viewed_3x  — user has viewed the dashboard ≥3 times
 *   2. subscribed_to_alerts — user subscribed to web-push or email alerts
 *   3. repeat_visits        — ≥3 page-views AND ≥3 distinct calendar days
 *
 * Per `.claude/rules/pwa-patterns.md`:
 *   - Counters live in localStorage and reflect REAL user behaviour — never seeded.
 *   - A `__pwaTestHelpers` object is exposed on `window` in non-production
 *     builds so Playwright tests can deterministically trigger moments.
 */

import { useEffect, useState } from "react";
import {
  DASHBOARD_VIEW_THRESHOLD,
  EXPOSE_TEST_HELPERS,
  PWA_STORAGE_KEYS,
  REPEAT_VISIT_COUNT_THRESHOLD,
  REPEAT_VISIT_DAY_THRESHOLD,
  type ValueMoment,
} from "#/lib/pwa/config.js";

// ---------------------------------------------------------------------------
// Pure storage helpers — no side effects, easy to unit-test.
// ---------------------------------------------------------------------------

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Privacy-mode or storage quota — silently ignore.
  }
}

function getVisitCount(): number {
  return Math.max(0, Number(safeGetItem(PWA_STORAGE_KEYS.visitCount) ?? "0"));
}

function getFirstVisitDate(): number {
  return Number(safeGetItem(PWA_STORAGE_KEYS.firstVisitDate) ?? "0");
}

function getDashboardViewCount(): number {
  return Math.max(
    0,
    Number(safeGetItem(PWA_STORAGE_KEYS.dashboardViewCount) ?? "0"),
  );
}

function getStoredMoment(): ValueMoment | null {
  const v = safeGetItem(PWA_STORAGE_KEYS.valueMomentReached);
  return v ? (v as ValueMoment) : null;
}

function persistMoment(moment: ValueMoment): void {
  // Don't overwrite an already-set moment — first one wins.
  if (getStoredMoment()) return;
  safeSetItem(PWA_STORAGE_KEYS.valueMomentReached, moment);
}

/** Count how many distinct calendar days the first-visit-date and today span. */
function distinctDaysSinceFirstVisit(): number {
  const first = getFirstVisitDate();
  if (!first) return 0;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((Date.now() - first) / msPerDay) + 1;
}

/** Evaluate all three triggers and return which fired (or null). */
export function evaluateValueMoment(): ValueMoment | null {
  const stored = getStoredMoment();
  if (stored) return stored;

  if (getDashboardViewCount() >= DASHBOARD_VIEW_THRESHOLD) {
    return "dashboard_viewed_3x";
  }

  const visits = getVisitCount();
  const days = distinctDaysSinceFirstVisit();
  if (
    visits >= REPEAT_VISIT_COUNT_THRESHOLD &&
    days >= REPEAT_VISIT_DAY_THRESHOLD
  ) {
    return "repeat_visits";
  }

  return null;
}

/** Increment the page-visit counter; record first-visit date on initial call. */
export function bumpPageVisit(): void {
  if (typeof window === "undefined") return;
  const prev = getVisitCount();
  const next = Number.isFinite(prev) ? prev + 1 : 1;
  safeSetItem(PWA_STORAGE_KEYS.visitCount, String(next));

  if (!getFirstVisitDate()) {
    safeSetItem(PWA_STORAGE_KEYS.firstVisitDate, String(Date.now()));
  }

  const fired = evaluateValueMoment();
  if (fired) persistMoment(fired);
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export interface PWAValueMomentState {
  valueMomentReached: boolean;
  trigger: ValueMoment | null;
  /** Call when the user successfully views the dashboard. */
  onDashboardViewed: () => void;
  /** Call when the user subscribes to push or email alerts. */
  onSubscribedToAlerts: () => void;
}

export function usePWAValueMoment(): PWAValueMomentState {
  const [trigger, setTrigger] = useState<ValueMoment | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Read initial stored state.
    const current = evaluateValueMoment();
    if (current) {
      persistMoment(current);
      setTrigger(current);
    }

    // Expose test helpers in non-production builds.
    if (EXPOSE_TEST_HELPERS) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__pwaTestHelpers = {
        reachValueMoment: (moment: ValueMoment = "subscribed_to_alerts") => {
          safeSetItem(PWA_STORAGE_KEYS.valueMomentReached, moment);
          setTrigger(moment);
        },
        resetPwaState: () => {
          for (const key of Object.values(PWA_STORAGE_KEYS)) {
            try {
              localStorage.removeItem(key);
            } catch {
              // ignore
            }
          }
          setTrigger(null);
        },
        getState: () => ({
          visitCount: getVisitCount(),
          dashboardViewCount: getDashboardViewCount(),
          firstVisitDate: getFirstVisitDate(),
          storedMoment: getStoredMoment(),
        }),
      };
    }
  }, []);

  const onDashboardViewed = (): void => {
    const prev = getDashboardViewCount();
    const next = prev + 1;
    safeSetItem(PWA_STORAGE_KEYS.dashboardViewCount, String(next));
    if (next >= DASHBOARD_VIEW_THRESHOLD) {
      persistMoment("dashboard_viewed_3x");
      setTrigger("dashboard_viewed_3x");
    }
  };

  const onSubscribedToAlerts = (): void => {
    persistMoment("subscribed_to_alerts");
    setTrigger("subscribed_to_alerts");
  };

  return {
    valueMomentReached: trigger !== null,
    trigger,
    onDashboardViewed,
    onSubscribedToAlerts,
  };
}
